import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { formatPlayCount, getRelated, getVideoById } from "@/data/videos";
import VideoCard from "@/components/base/VideoCard";
import SectionHeading from "@/components/base/SectionHeading";

export default function Detail() {
  const { id = "" } = useParams();
  const video = getVideoById(id);
  const [showNotice, setShowNotice] = useState(false);

  useDocumentMeta({
    title: video ? `${video.title}详情介绍 - 51无敌电影网` : "影片未找到 - 51无敌电影网",
    description: video
      ? `${video.title}（${video.year}·${video.region}）简介与主演信息：${video.synopsis.slice(0, 60)}`
      : "未找到对应的影视作品，请返回浏览其他内容。",
    keywords: video
      ? `${video.title},${video.genres.join(",")},${video.actors.slice(0, 2).join(",")},51无敌电影网`
      : "51无敌电影网,影视详情",
  });

  if (!video) {
    return (
      <div className="w-full px-4 md:px-6 py-24 flex flex-col items-center justify-center text-center">
        <span className="w-16 h-16 rounded-full bg-background-200 text-foreground-400 flex items-center justify-center">
          <i className="ri-film-line text-3xl"></i>
        </span>
        <h1 className="mt-5 text-xl font-heading font-semibold text-foreground-900">
          未找到该影视作品
        </h1>
        <p className="mt-2 text-sm text-foreground-500">该内容可能已下线或链接有误</p>
        <Link
          to="/"
          className="mt-6 px-6 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-semibold transition-colors cursor-pointer whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelated(video, 6);
  const categoryLink = video.type === "movie" ? "/category/movie" : "/category/tv";
  const categoryLabel = video.type === "movie" ? "电影" : "电视剧";

  const infoRows = [
    { label: "导演", value: video.director },
    { label: "主演", value: video.actors.join(" / ") },
    { label: "地区", value: video.region },
    { label: "年份", value: `${video.year}` },
    { label: "类型", value: video.genres.join(" / ") },
    ...(video.type === "tv" ? [{ label: "集数", value: `全 ${video.episodes} 集` }] : []),
    { label: "更新时间", value: video.updatedAt },
  ];

  return (
    <div className="w-full">
      <section className="relative w-full overflow-hidden bg-primary-950">
        <img
          src={video.banner || video.cover}
          alt={`${video.title}主视觉`}
          title={`${video.title} - 51无敌电影网`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 via-foreground-950/80 to-foreground-950/50" />

        <div className="relative z-10 w-full px-4 md:px-6 py-8 md:py-12">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs md:text-sm text-background-200 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={categoryLink} className="hover:text-accent-400 transition-colors cursor-pointer">
              {categoryLabel}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50">{video.title}</span>
          </nav>

          <div className="flex flex-col sm:flex-row gap-5 md:gap-7">
            <div className="w-32 md:w-44 shrink-0 rounded-lg overflow-hidden">
              <img
                src={video.cover}
                alt={`${video.title}海报`}
                title={`${video.title} 在线观看`}
                className="w-full aspect-[2/3] object-cover object-top"
              />
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50">
                {video.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-100">
                <span className="flex items-center gap-1 text-accent-400 font-label font-semibold text-base">
                  <i className="ri-star-fill"></i>
                  {video.rating.toFixed(1)}
                </span>
                <span>{video.year}</span>
                <span>{video.region}</span>
                <span>{video.genres.join(" / ")}</span>
                <span className="flex items-center gap-1">
                  <i className="ri-fire-line"></i>
                  {formatPlayCount(video.playCount)}次播放
                </span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {video.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-md bg-background-50/15 text-background-100 text-xs font-label backdrop-blur-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <p className="mt-5 text-sm md:text-base text-background-200 leading-relaxed max-w-3xl">
                {video.synopsis}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowNotice(true)}
                  className="px-6 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-label font-semibold flex items-center gap-2 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-circle-fill text-lg"></i>
                  立即播放
                </button>
                <Link
                  to={categoryLink}
                  className="px-6 py-2.5 rounded-md bg-background-50/15 hover:bg-background-50/25 text-background-50 text-sm font-label font-semibold flex items-center gap-2 transition-colors backdrop-blur-sm cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回{categoryLabel}频道
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-6 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2 space-y-6">
            <section className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
              <h4 className="flex items-center gap-2 text-lg font-heading font-semibold text-foreground-900">
                <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
                  <i className="ri-file-text-line"></i>
                </span>
                影视简介
              </h4>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                {video.synopsis}
              </p>
              <p className="mt-3 text-sm md:text-base text-foreground-700 leading-relaxed">
                《{video.title}》由{video.director}执导，{video.actors.join("、")}联袂出演，
                是一部值得关注的{video.region}{categoryLabel}作品，类型涵盖{video.genres.join("、")}。
              </p>
            </section>

            <section className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
              <h4 className="flex items-center gap-2 text-lg font-heading font-semibold text-foreground-900">
                <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-600 flex items-center justify-center">
                  <i className="ri-play-circle-line"></i>
                </span>
                播放入口（演示区）
              </h4>
              <div className="mt-4 relative aspect-video rounded-lg overflow-hidden bg-foreground-950 flex flex-col items-center justify-center text-center">
                <div className="absolute inset-0 opacity-30">
                  <img
                    src={video.banner || video.cover}
                    alt={`${video.title}播放预览`}
                    title={`${video.title}播放示范区`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="relative z-10 px-6">
                  <button
                    type="button"
                    onClick={() => setShowNotice(true)}
                    className="w-16 h-16 rounded-full bg-background-50/90 text-primary-600 flex items-center justify-center mx-auto hover:scale-105 transition-transform cursor-pointer"
                    aria-label="播放演示"
                  >
                    <i className="ri-play-fill text-3xl"></i>
                  </button>
                  <p className="mt-4 text-background-50 font-label text-sm">演示播放器 · 点击查看说明</p>
                </div>
              </div>
              <div className="mt-4 flex items-start gap-2 rounded-lg bg-background-100 p-3.5">
                <i className="ri-information-line text-accent-600 text-lg mt-0.5"></i>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                  温馨提示：本区域为播放功能演示示范区，仅用于展示页面效果，暂不提供真实在线播放。
                  真实播放能力将在后续接入内容服务后开放。
                </p>
              </div>
            </section>
          </div>

          <aside className="lg:col-span-1">
            <section className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6 lg:sticky lg:top-20">
              <h4 className="flex items-center gap-2 text-lg font-heading font-semibold text-foreground-900">
                <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-600 flex items-center justify-center">
                  <i className="ri-information-line"></i>
                </span>
                影片信息
              </h4>
              <dl className="mt-4 space-y-3">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <dt className="w-16 shrink-0 text-foreground-400 font-label">{row.label}</dt>
                    <dd className="text-foreground-800 leading-relaxed">{row.value}</dd>
                  </div>
                ))}
              </dl>
              <Link
                to={categoryLink}
                className="mt-5 flex items-center justify-center gap-2 w-full py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-semibold transition-colors cursor-pointer"
              >
                <i className="ri-apps-2-line"></i>
                查看全部{categoryLabel}
              </Link>
            </section>
          </aside>
        </div>

        <section className="mt-10 md:mt-12">
          <SectionHeading
            icon="ri-magic-line"
            title="相关推荐"
            subtitle="根据类型为你智能关联相似作品"
            moreLink={categoryLink}
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {related.map((item) => (
              <VideoCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      </div>

      {showNotice && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/50 px-4 animate-fade-in"
          onClick={() => setShowNotice(false)}
        >
          <div
            className="w-full max-w-sm rounded-lg bg-background-50 p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="w-14 h-14 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center mx-auto">
              <i className="ri-play-circle-line text-2xl"></i>
            </span>
            <h3 className="mt-4 text-lg font-heading font-semibold text-foreground-900">
              播放功能为演示性质
            </h3>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
              本网站暂未接入真实在线播放能力，此入口仅作页面演示。
              未来接入内容服务后即可正常观看《{video.title}》。
            </p>
            <button
              type="button"
              onClick={() => setShowNotice(false)}
              className="mt-5 w-full py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-semibold transition-colors cursor-pointer"
            >
              我知道了
            </button>
          </div>
        </div>
      )}
    </div>
  );
}